import Button from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import HelenInfoBlock from '@/compositions/HelenInfoBlock'
import { coreLayoutContainer, coreLayoutSection, rbp } from '@/constants/measured-scope'
import { useAuth } from '@/context/auth'
import { useFeatureToggle } from '@/context/featureToggles'
import type { PageSectionUnion, PartnerInfoContent, PartnerInfoPageSection } from '@/shared/api-generated-types'
import Modal from '@/widgets/Modal'
import PartnerInfo from '@/widgets/PartnerInfo'
import { useMediaQuery } from '@react-hookz/web'
import classNames from 'classnames/bind'
import DOMPurify from 'isomorphic-dompurify'
import Link from 'next/link'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as styles from './style'

const cx = classNames.bind(styles)

export interface Props extends Partial<PartnerInfoContent> {
  className?: string
  /** When provided, clicking the logo/sponsored-by opens the welcome popup instead of sponsor info */
  onLogoClick?: () => void
}

export function isPartnerInfoSection(cpaSection: PageSectionUnion): cpaSection is PartnerInfoPageSection {
  return cpaSection.__typename === 'PartnerInfoPageSection'
}

const PartnerInfoBlock = ({ className = '', onLogoClick, ...props }: Props): JSX.Element => {
  const showHelenTab = useFeatureToggle('helen_tab')

  const { t } = useTranslation('common')

  const {
    state: { user },
  } = useAuth()

  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.smallTablet})`, {
    initializeWithValue: false,
  })
  const iconSize = !isLargeDevice ? 10 : 14
  const classes = cx({
    root: true,
    [className]: true,
  })

  const [partnerInfoPopup, setPartnerInfoPopup] = useState<boolean>(false)

  const togglePartnerInfoPopupClose = (): void => {
    // If partner has a custom welcome popup, clicking logo opens that instead
    if (onLogoClick) {
      onLogoClick()
      return
    }
    setPartnerInfoPopup(!partnerInfoPopup)
  }

  const hideInfoForAuthUser = useMemo(() => {
    if (!isLargeDevice && user) {
      return true
    }
    return false
  }, [user, !isLargeDevice])

  if (showHelenTab && props.helenEnabled)
    return (
      <HelenInfoBlock
        partner={{ description: props.description, logo: props.logo, name: props.name }}
        userStats={props?.userStats}
        onLogoClick={onLogoClick}
      />
    )
  return (
    <header className={`${coreLayoutSection}`}>
      {/* Rendering "logged-in" header logic off of userStats prop as this prop will only be true if the user is logged in. */}
      {user ? (
        <Stack y className={`${coreLayoutContainer}`}>
          <Stack y justifyContent="flex-start" alignItems="start" spaceBetweenX="lg" className="relative">
            {partnerInfoPopup ? (
              <Modal onDismiss={togglePartnerInfoPopupClose}>
                <PartnerInfo
                  onClickCancel={togglePartnerInfoPopupClose}
                  description={props.description}
                  logo={props.logo}
                  name={props.name}
                />
              </Modal>
            ) : null}
            <Stack x alignItems="start" justifyContent="start" className="mb-sm mb-0-gte-md">
              <Stack y alignItems="start" alignSelf="center" className={styles.memberInfo}>
                <Text
                  weight="lg"
                  size="lg"
                  responsiveSize={{ sm: 'xl', xl: 'xxl' }}
                  className={`${styles.memberName} fc-primary`}
                >
                  {props.title}
                </Text>
                <Link href="/notes" className={`${styles.profileLink} fs-xxs fs-xs-gte-lg mb-sm mb-md-gte-lg`}>
                  <IcoMoon icon="user" className="mr-xxxs" />
                  {t('cta.viewProfile')}
                </Link>
              </Stack>
              {props.logo ? (
                <Stack className="mb-xxs mb-sm-gte-sm">
                  <a className={styles.sponsoredText} onClick={togglePartnerInfoPopupClose}>
                    <Stack x justifyContent="flex-end" alignItems="center" className="mb-0 mb-xxs-gte-sm">
                      <IcoMoon icon="info" size={iconSize} className="mr-xxxs" />
                      <Text>Sponsored By</Text>
                    </Stack>
                    <div className={`${styles.logo} ${styles.logoMember}`}>
                      <img src={props.logo} alt={props.name ?? ''} data-testid="logo" loading="lazy" />
                    </div>
                  </a>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack x className={`${coreLayoutContainer} ${classes}`} alignItems="center" spaceBetweenX="lg">
          <Stack className={styles.ctaBlock} spaceBetweenY="sm" y>
            {props.logo ? (
              <div className={`${styles.logo} mb-xxs mb-sm-gte-md`}>
                <img src={props.logo} alt={props.name ?? ''} data-testid="logo" loading="lazy" />
              </div>
            ) : null}
            {props.title ? (
              <Text
                weight="lg"
                size="xl"
                responsiveSize={{
                  lg: 'xxl',
                  xxl: 'xxxl',
                }}
                className="fc-primary"
              >
                <span dangerouslySetInnerHTML={{ __html: props.title.replace(/<[^>]+>/g, '') }} />
              </Text>
            ) : null}
            {props.description && !hideInfoForAuthUser ? (
              <Text weight="sm">
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(props.description.replace(/<[^>]+>/g, '')),
                  }}
                />
              </Text>
            ) : null}
            {props.primaryAction ? (
              <div className={`${styles.ctaButtons} mt-xs`}>
                <Button
                  theme="primary"
                  label={props.primaryAction.label}
                  className={styles.ctaButton}
                  size="md"
                  component="link"
                  href={props.primaryAction.url}
                />
                {props.navigationActions && props.navigationActions.length ? (
                  <div>
                    {props.navigationActions.map((action, index) => (
                      <Button
                        label={action.label}
                        className={styles.ctaButton}
                        size="md"
                        component="link"
                        theme="clear"
                        href={action.url}
                        key={index}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </Stack>
          {props.showVideo && props.video && !hideInfoForAuthUser ? (
            <div className={styles.videoContainer}>
              <div>
                <video poster={props.video.posterUrl} data-testid="video" className={styles.video} controls>
                  <source src={`${props.video.srcUrl}#t=0.001`} type="video/mp4" />
                </video>
              </div>
            </div>
          ) : null}
        </Stack>
      )}
    </header>
  )
}

export default PartnerInfoBlock
